/**
 * Modal Module
 * Handles various types of modal content display
 *
 * @module modal-module
 */

import { ErrorTypes, WeatherAppError } from '../utils/error-handler';

/**
 * Initialize modal functionality
 */
export function initModals() {
  // Set up close buttons for all modals
  document.querySelectorAll('.close-modal').forEach(button => {
    button.addEventListener('click', () => {
      const modal = button.closest('.modal');
      if (modal) {
        modal.style.display = 'none';
      }
    });
  });

  // Close modals when clicking outside content
  document.querySelectorAll('.modal').forEach(modal => {
    modal.addEventListener('click', event => {
      if (event.target === modal) {
        modal.style.display = 'none';
      }
    });
  });

  // Create content modal if it doesn't exist
  createContentModal();
}

/**
 * Create the content modal if it doesn't exist
 */
function createContentModal() {
  if (document.getElementById('content-modal')) {
    return;
  }

  const modalHtml = `
    <div id="content-modal" class="modal" role="dialog" aria-modal="true" aria-labelledby="content-modal-title">
      <div class="modal-content">
        <button class="close-modal" aria-label="Close content">&times;</button>
        <h2 id="content-modal-title" class="modal-title">External Content</h2>
        <div id="content-container" class="content-container"></div>
      </div>
    </div>
  `;

  const modalElement = document.createElement('div');
  modalElement.innerHTML = modalHtml;
  document.body.appendChild(modalElement.firstElementChild);

  // Add event listener to close button
  document.querySelector('#content-modal .close-modal').addEventListener('click', () => {
    closeContentModal();
  });

  // Close when clicking outside content
  document.getElementById('content-modal').addEventListener('click', event => {
    if (event.target === document.getElementById('content-modal')) {
      closeContentModal();
    }
  });
}

/**
 * Opens a modal with external content
 * @param {string} url - The URL to display
 * @param {string} title - The title for the modal
 * @param {string} type - The type of content (iframe, image, youtube, etc.)
 */
export function showContentModal(url, title, type = 'iframe') {
  // For sites that should open in new tabs (YouTube, search engines, etc.)
  if (
    url.includes('youtube.com') ||
    url.includes('youtu.be') ||
    url.includes('google.com') ||
    url.includes('bing.com') ||
    url.includes('hazcams.com')
  ) {
    window.open(url, '_blank', 'noopener');
    return;
  }

  // For weather radar and maps, use modal
  const isWeatherMap =
    url.includes('openweathermap') ||
    url.includes('weather.gov') ||
    url.includes('radar') ||
    type === 'map';

  // Get the modal element
  const contentModal = document.getElementById('content-modal');
  if (!contentModal) {
    console.error('Content modal element not found');
    // Open in new window as fallback
    window.open(url, '_blank', 'noopener');
    return;
  }

  // Show the modal
  contentModal.style.display = 'block';

  // Set the title
  const modalTitle = document.getElementById('content-modal-title');
  if (modalTitle) {
    modalTitle.textContent = title || 'External Content';
  }

  // Get the content container
  const contentContainer = document.getElementById('content-container');
  if (!contentContainer) {
    console.error('Content container element not found');
    return;
  }

  // Clear previous content
  contentContainer.innerHTML = '';

  try {
    // Create content based on type
    switch (type.toLowerCase()) {
      case 'iframe':
        createIframeContent(contentContainer, url, title);
        break;
      case 'image':
        createImageContent(contentContainer, url, title);
        break;
      case 'video':
        createVideoContent(contentContainer, url, title);
        break;
      default:
        createIframeContent(contentContainer, url, title);
    }
  } catch (error) {
    console.error('Error showing content:', error);
    contentContainer.innerHTML = `
      <div class="error-container">
        <div class="error-icon">❌</div>
        <div class="error-content">
          <p>Unable to load content. Please try opening it directly.</p>
          <a href="${url}" target="_blank" rel="noopener" class="modal-button">Open in New Window</a>
        </div>
      </div>
    `;
  }
}

/**
 * Creates iframe content in the container
 */
function createIframeContent(container, url, title) {
  container.innerHTML = `
    <div class="iframe-container">
      <iframe
        src="${url}"
        title="${title || 'External content'}"
        width="100%"
        height="100%"
        frameborder="0"
        allowfullscreen>
      </iframe>
    </div>
  `;
}

/**
 * Creates image content in the container
 */
function createImageContent(container, url, title) {
  container.innerHTML = `
    <div class="image-container">
      <img src="${url}" alt="${title || 'Image'}" />
    </div>
  `;
}

/**
 * Creates video content in the container
 */
function createVideoContent(container, url, title) {
  container.innerHTML = `
    <div class="video-container">
      <video controls autoplay muted>
        <source src="${url}" type="video/mp4">
        Your browser does not support the video tag.
      </video>
      <div class="modal-overlay-controls">
        <a href="${url}" target="_blank" rel="noopener" class="modal-button">Open Original Video</a>
      </div>
    </div>
  `;
}

/**
 * Closes the content modal
 */
export function closeContentModal() {
  const contentModal = document.getElementById('content-modal');
  if (contentModal) {
    contentModal.style.display = 'none';
  }
}

/**
 * Determines the content type based on URL
 * @param {string} url - The URL to check
 * @returns {string} The content type
 */
export function getContentType(url) {
  if (!url) return 'iframe';

  // Check for image extensions
  if (/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url)) {
    return 'image';
  }

  // Check for video extensions
  if (/\.(mp4|webm|ogg|mov)$/i.test(url)) {
    return 'video';
  }

  // Default to iframe
  return 'iframe';
}
